import { useEffect, useMemo, useState } from 'react';
import { isColourway, useSubmodels } from './listingStore';
import { submodelLabel, submodelName } from './variantLabel';
import { CenteredSelect } from './CenteredSelect';
import { setSubmodelFilter, useSubmodelFilter } from './submodelFilterStore';
import { useColourwayInView } from './colourwayPaint';
import { appliedReading } from './materialsRun';
import { repaintKept } from './partMaterialRun';
import { useLanguage } from '../../contexts/LanguageContext';
import { IS_DEMO } from '../../apiBase';

// A document's submodel options: one per listing link, IDENTIFIED BY ITS ID and shown by
// its label. The id is the link's own uuid — it does not move when a re-read rewords the
// materials, which is what filing anything under the label could not promise. The label is
// numbered and falls back to the link's rank alone, so a submodel with nothing read yet is
// still pickable and still tells you which link it is. See submodelLabel.
//
// Shared by both filter bars and their per-submodel logic (Phase 2's cover + context,
// Phase 5's video grounding).
// `name` is the label without its number, for the picker, which prints the number apart.
export type SubmodelOption = { id: string; label: string; name: string };

export function useSubmodelOptions(docId: string): SubmodelOption[] {
  const { t } = useLanguage();
  const submodels = useSubmodels(docId);
  return useMemo(
    // Numbered by the row's place among the links, as the listings table numbers it.
    () =>
      submodels.flatMap((s, i) => (isColourway(submodels, s) ? [{
        id: s.id,
        label: submodelLabel(s, i, t('notice.listing.label')),
        name: submodelName(s, t('notice.listing.label')),
      }] : [])),
    [submodels, t],
  );
}

// KEEPING THE CHOICE ON A REAL SUBMODEL: the first, until the user picks another — and back
// to the first when the one picked is removed. Every phase reads the filter to decide what it
// shows, so this runs in the viewer, for as long as a document is open, whichever phase is on
// screen and whether or not a picker is drawn (the demo draws none).
export function useParkedSubmodelFilter(docId: string) {
  const options = useSubmodelOptions(docId);
  const filter = useSubmodelFilter(docId);
  useEffect(() => {
    if (options.length && !options.some((o) => o.id === filter)) {
      setSubmodelFilter(docId, options[0].id);
    }
  }, [options, filter, docId]);
  // AND THE MESHES DRAWN IN IT — see colourwayPaint. A colourway with a reading applied and no
  // paint stored for it (every one, the first time a manual opens after the shape became one) has
  // its paint written, from that reading, as applying it again would.
  const [painted, setPainted] = useState<Set<string> | null>(null);
  useColourwayInView(docId, filter, setPainted);
  useEffect(() => {
    if (!painted || IS_DEMO) return;
    for (const o of options) {
      if (painted.has(o.id) || !appliedReading(docId, o.id)) continue;
      // Once: the set is this page's record of what it has asked for.
      painted.add(o.id);
      repaintKept(docId, o.id);
    }
  }, [painted, options, docId]);
}

// THE ONE PLACE THE SUBMODEL IS CHOSEN. It was a dropdown inside four phases — and read,
// without one, by two more — so changing colourway meant going back to one of them. It is
// document-wide, so it lives with the document: in the desktop sidebar (see DocSidebar), under
// the run pager, on every phase — a white pill named inside, its name on as many lines as it takes.
//
// Nothing with a single submodel: there is no choice to offer. Nothing in the demo either: a
// demo document is shown as one product (the value is still parked, see above).
//
// GREYED, NOT HIDDEN, on a phase that does not read it (`idle`) — and still opens, to show the
// choices, none of them pickable: the sidebar keeps its shape
// from phase to phase, and the choice is still the one the next phase that reads it will use.
export function SubmodelPicker({ docId, idle = false }: { docId: string; idle?: boolean }) {
  const { t } = useLanguage();
  const options = useSubmodelOptions(docId);
  const filter = useSubmodelFilter(docId);
  if (options.length < 2 || IS_DEMO) return null;
  return (
    <div className="mt-4 border-t border-slate-600 pt-4">
      <CenteredSelect
        value={filter}
        options={options.map((o, i) => ({ value: o.id, label: o.name, tag: String(i + 1) }))}
        onChange={(v) => setSubmodelFilter(docId, v)}
        ariaLabel={t('notice.step4.submodelFilter')}
        look="pill"
        block
        prefix={t('notice.step4.submodelFilter')}
        readOnly={idle}
      />
    </div>
  );
}
